import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import AddIcon from "@mui/icons-material/Add";
import { Button, Modal, Stack, styled, Typography } from "@mui/material";
import {
  CreateAssetClass,
  CreateSubAssetClass,
  IManageAssetClassesContext,
  ManageAssetClassesContext,
  Section
} from "components/Admins-asset-classes";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useModals } from "hooks";
import { IApiServiceError, useDealActions } from "store";
import { IAssetClass } from "store/types";
import { toastify } from "utils/tyostify";

const StyledHeadText = styled(Typography)(() => ({
  fontSize: "14px",
  lineHeight: "16px",
  color: "#071F35",
  fontWeight: 500
}));

type CreateNewSubAssetClassState = {
  onSubmit: (name: string) => void;
  onCancel: () => void;
  checkIsValidName: (name: string) => string | null;
} | null;

const isHiddenErrorMsg =
  "This Asset class is currently in use and cannot be hidden. Please remove or replace it in all deals referencing it before hiding the Asset class.";

const isHiddenMultipleErrorMsg =
  "These Asset classes are currently in use and cannot be hidden. Please remove or replace them in all deals referencing them before hiding the Asset classes.";

interface AssetClassesTabProps {
  data: IAssetClass[];
  loading: boolean;
  setData: React.Dispatch<React.SetStateAction<IAssetClass[] | undefined>>;
}

export const AssetClassesTab: React.FC<AssetClassesTabProps> = ({ data, loading, setData }) => {
  const [isCreateNewAsset, setIsCreateNewAsset] = useState(false);
  const [createNewSubAssetClass, setCreateNewSubAssetClass] = useState<CreateNewSubAssetClassState>(null);

  const newFormRef = useRef<HTMLDivElement>(null);
  const customAssetClassesBodyRef = useRef<HTMLDivElement>(null);

  const {
    updateAssetClassAccountDataAction,
    setVisibilitySubAssetClassesAction,
    deleteAssetClassesAction,
    createAssetClassesAction
  } = useDealActions();
  const { errorModal } = useModals();

  const { systemAssetClasses, customAssetClasses } = useMemo<{
    systemAssetClasses: IAssetClass[];
    customAssetClasses: IAssetClass[];
  }>(() => {
    return data.reduce(
      (acc: { systemAssetClasses: IAssetClass[]; customAssetClasses: IAssetClass[] }, item) => {
        if (item.account_id) {
          acc.customAssetClasses.push(item);
        } else {
          acc.systemAssetClasses.push(item);
        }
        return acc;
      },
      { systemAssetClasses: [], customAssetClasses: [] }
    );
  }, [data]);

  const updateAssetClass = useCallback<IManageAssetClassesContext["updateAssetClass"]>(async (data) => {
    try {
      const updatedAssetClass = await updateAssetClassAccountDataAction(data);
      setData((prev = []) => prev.map((item) => (item.id === data.id ? updatedAssetClass : item)));

      toast.success("Asset class updated successfully");
    } catch (error) {
      // Handle is hidden error separately
      if ((error as IApiServiceError)?.errors["is_hidden"]) {
        errorModal({
          title: "Error message",
          msg: isHiddenErrorMsg
        });
      } else {
        toast.error((error as IApiServiceError).errors["name"][0]);
      }
      throw error; // Throw error to revert the optimistic update
    }
  }, []);

  const updateSubAssetClass = useCallback<IManageAssetClassesContext["updateSubAssetClass"]>(
    async ({ assetClassId, subAssetClassId, ...body }) => {
      try {
        await updateAssetClassAccountDataAction({ id: subAssetClassId, ...body });
        setData((prev = []) => {
          return prev.map((item) => {
            if (item.id === assetClassId) {
              return {
                ...item,
                sub_asset_class: item.sub_asset_class.map((subAssetClass) =>
                  subAssetClass.id === subAssetClassId ? { ...subAssetClass, ...body } : subAssetClass
                )
              };
            }
            return item;
          });
        });
        toast.success("Asset class updated successfully");
      } catch (error) {
        // Handle is hidden error separately
        if ((error as IApiServiceError)?.errors["is_hidden"]) {
          errorModal({
            title: "Error message",
            msg: isHiddenErrorMsg
          });
        } else {
          toast.error((error as IApiServiceError).message);
        }
        throw error;
      }
    },
    []
  );

  const setIsHiddenSubAssets = useCallback<IManageAssetClassesContext["setIsHiddenSubAssets"]>(
    async ({ assetClassId, is_hidden }) => {
      try {
        const { sub_asset_class } = data.find((item) => item.id === assetClassId) || {};
        if (!sub_asset_class) return;

        await setVisibilitySubAssetClassesAction({
          asset_classes_ids: sub_asset_class.map(({ id }) => id),
          is_hidden
        });

        setData((prev = []) => {
          return prev.map((item) => {
            if (item.id === assetClassId)
              return {
                ...item,
                sub_asset_class: sub_asset_class.map((subAssetClass) => ({ ...subAssetClass, is_hidden }))
              };
            return item;
          });
        });
      } catch (error) {
        errorModal({
          title: "Error message",
          msg: isHiddenMultipleErrorMsg
        });

        throw error;
      }
    },
    []
  );

  const createSubAsset = useCallback<IManageAssetClassesContext["createSubAsset"]>(async ({ assetClassId }) => {
    try {
      const name = await getSubAssetName(() => null);

      if (!name) return;

      const newSubAsset = await createAssetClassesAction({ name, parent_id: assetClassId });

      setData((prev = []) => {
        return prev.map((item) =>
          item.id === assetClassId ? { ...item, sub_asset_class: [newSubAsset, ...item.sub_asset_class] } : item
        );
      });
      toast.success("Sub asset class created successfully");
    } catch (error) {
      toast.error((error as IApiServiceError).errors["name"][0]);
    }
  }, []);

  const deleteSubAsset = useCallback<IManageAssetClassesContext["deleteSubAsset"]>(
    async ({ assetClassId, subAssetClassId }) => {
      try {
        // Optimistic update
        setData((prev = []) => {
          return prev.map((item) => {
            if (item.id === assetClassId)
              return { ...item, sub_asset_class: item.sub_asset_class.filter(({ id }) => id !== subAssetClassId) };
            return item;
          });
        });

        await deleteAssetClassesAction(subAssetClassId);
      } catch (error) {
        const errorMessage = (error as IApiServiceError).message;
        toastify({ message: errorMessage, type: "error" });
        throw error; // Throw error to revert the optimistic update
      }
    },
    []
  );

  const deleteAssetClass = useCallback<IManageAssetClassesContext["deleteAssetClass"]>(async (id) => {
    try {
      await deleteAssetClassesAction(id);
      setData((prev = []) => prev.filter((item) => item.id !== id));
      toast.success("Asset class deleted successfully");
    } catch (error) {
      const errorMessage = (error as IApiServiceError).message;
      toastify({ message: errorMessage, type: "error" });
      throw error; // Throw error to revert the optimistic update
    }
  }, []);

  const getSubAssetName = useCallback(
    async (checkIsValidName: (name: string) => string | null): Promise<string | void> => {
      // Prompt the user to enter the name of the new sub asset class
      const newSubAssetClassName = await new Promise<string>((resolve, reject) => {
        setCreateNewSubAssetClass({
          checkIsValidName,
          onSubmit: (name) => {
            resolve(name);
            setCreateNewSubAssetClass(null);
          },
          onCancel: reject
        });
      }).catch(() => {
        setCreateNewSubAssetClass(null);
      });

      return newSubAssetClassName;
    },
    []
  );

  const onAddNew = () => {
    if (isCreateNewAsset && newFormRef.current) {
      newFormRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setIsCreateNewAsset(true);
      customAssetClassesBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    if (!createNewSubAssetClass) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        createNewSubAssetClass.onCancel();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [createNewSubAssetClass]);

  return (
    <Stack
      sx={{
        bgcolor: "#FFFFFF",
        flexGrow: 1,
        overflow: "hidden",
        gap: "16px",
        p: "16px",
        borderRadius: "8px",
        alignItems: "stretch",
        boxShadow: "0 1px 2px 0 #1212171a",
        margin: "0 2px 2px 2px"
      }}
    >
      <AsyncContentMui spinnerProps={{ size: 120 }} isLoading={loading}>
        <ManageAssetClassesContext.Provider
          value={{
            getSubAssetName,
            updateAssetClass,
            updateSubAssetClass,
            setIsHiddenSubAssets,
            deleteSubAsset,
            createSubAsset,
            deleteAssetClass
          }}
        >
          <Stack sx={{ gap: "16px", overflow: "hidden", flexGrow: 1 }}>
            <StyledHeadText>System Asset Classes</StyledHeadText>

            {/* Asset Class Form Block */}
            <Section data={systemAssetClasses} flex="65%" />
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: "16px",
                justifyContent: "space-between",
                flexShrink: 0
              }}
            >
              <StyledHeadText>Custom Asset Classes</StyledHeadText>
              <Button size="sm" variant="contained" startIcon={<AddIcon />} onClick={onAddNew}>
                Add New
              </Button>
            </Stack>
            <Section
              data={customAssetClasses}
              hideSystemAssetClass
              bodyRef={customAssetClassesBodyRef}
              {...(isCreateNewAsset && {
                createNewAssetForm: (
                  <CreateAssetClass
                    formRef={newFormRef}
                    onSuccess={(data) => {
                      setData((prev = []) => [data, ...prev]);
                      setIsCreateNewAsset(false);
                    }}
                    onCancel={() => setIsCreateNewAsset(false)}
                  />
                )
              })}
              flex="35%"
            />
          </Stack>
        </ManageAssetClassesContext.Provider>
      </AsyncContentMui>
      <Modal onClose={createNewSubAssetClass?.onCancel} open={Boolean(createNewSubAssetClass)}>
        <CreateSubAssetClass
          checkIsValidName={createNewSubAssetClass?.checkIsValidName}
          onSubmit={createNewSubAssetClass?.onSubmit}
          onCancel={createNewSubAssetClass?.onCancel}
        />
      </Modal>
    </Stack>
  );
};
